import React from 'react';
import { Chip, TableCell, Tooltip } from '@mui/material';
import { RxAdjStateBadge } from '../../../../../components/common/RxStatusBadge';
import { RX_STATUS_WORD_COLORS, RX_STATUS_WORD_LABELS, type RxStatusWord } from '../../../../../patient/rxStatusWords';
import { describePmsGateShort } from '../../../../../components/order-fulfillment/pmsGateDisplay';
import { FillStatusChip } from '../../../../../components/order-fulfillment/FillStatusChip';
import type { PmsGateResult } from '../../../../../types/pmsGate';

/**
 * PRX-80 Task 13 — the two status cells of the prescriptions table, one
 * component per cell so the column chooser (Task 14) can hide and reorder
 * them independently and GP-1225's column adjacencies stay intact.
 *
 * A Fillware row arrives with `rxStatusWord` / `rxStatusReason` (Task 12); a
 * Kroll row carries null for both and keeps today's two chips exactly. A
 * Fillware row whose status letter the mapping does not recognise ALSO
 * arrives with a null word — and on Fillware `rx.Status` is that LETTER, so
 * falling through to the Kroll branch would push it through the numeric 1-8
 * map and render "Unknown (X)", a value the PMS never said. Both cells read
 * `—` there, and say why.
 *
 * Every reason here is a DESCRIPTION, never a name (fix round 2, item 4). An
 * MUI `Tooltip` with a string title sets the child's `aria-label`, so a screen
 * reader heard the reason INSTEAD of the status word — "Awaiting prescriber
 * callback" where the column exists to say "Hold". `describeChild` keeps the
 * child's own text as its accessible name and attaches the reason through
 * `aria-describedby`; the child is focusable so a keyboard user reaches it.
 */
export interface RxStatusCellProps {
  /** Null on every Kroll row, and on a Fillware row whose status letter this mapping does not recognise. */
  rxStatusWord: RxStatusWord | null;
  /** Rx.StatusChangeData, already nulled by the backend when it is only the "NO REASON" boilerplate. */
  rxStatusReason: string | null;
  /** `rx.Inactive` — drives today's "Rx Active" chip in the Kroll branch. */
  inactive: boolean;
  /** `rx.Status` — the numeric 1-8 Kroll dispense status; a letter on Fillware, where it is never rendered. */
  status: number;
  /** `rx.AdjState` — the Kroll adjudication flag, unchanged. */
  adjState?: number | null;
  /** The pharmacy's PMS; a null word on 'fillware' is an unrecognised status, not a Kroll row. */
  databaseType?: string | null;
  /** The server's fill-status verdict for the row (PRX-59). Read only on a Fillware row (PRX-165 B1). */
  pmsGate?: PmsGateResult | null;
}

const UNRECOGNISED_FILLWARE_STATUS = 'This Fillware status is not recognised';

/** Kroll's numeric 1-8 dispense status, in words. Also read by the status legend (PRX-137). */
export const DISPENSE_STATUS_LABELS: Record<number, string> = {
  1: 'Active',
  2: 'Cancelled',
  3: 'Unfilled',
  4: 'Not Dispensed',
  5: 'Deleted',
  6: 'On File',
  7: 'Exp. Svc (Cancelled)',
  8: 'Suspended',
};

const getDispenseStatusLabel = (status: number): string =>
  DISPENSE_STATUS_LABELS[status] || `Unknown (${status})`;

const getDispenseStatusColor = (status: number): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  if ([1, 6, 8].includes(status)) return 'success';
  if ([3, 4].includes(status)) return 'warning';
  if ([2, 5, 7].includes(status)) return 'error';
  return 'default';
};

const isUnrecognisedFillware = (props: RxStatusCellProps): boolean =>
  props.rxStatusWord == null && props.databaseType === 'fillware';

/**
 * The `—` an unrecognised Fillware status reads as, in either cell. The
 * sentence is a `describeChild` tooltip on a focusable span — not a native
 * `title` on the cell, which is announced inconsistently and cannot be reached
 * without a mouse.
 */
const UnrecognisedFillwareCell: React.FC = () => (
  <TableCell>
    <Tooltip describeChild title={UNRECOGNISED_FILLWARE_STATUS}>
      <span tabIndex={0} data-testid="rx-status-unrecognised">
        —
      </span>
    </Tooltip>
  </TableCell>
);

/** The "Rx Active" cell: the Fillware word chip, today's active/inactive chip on a Kroll row, or `—`. */
export const RxActiveStatusCell: React.FC<RxStatusCellProps> = (props) => {
  const { rxStatusWord, rxStatusReason, inactive } = props;
  if (isUnrecognisedFillware(props)) return <UnrecognisedFillwareCell />;
  if (rxStatusWord != null) {
    const chip = (
      <Chip
        label={RX_STATUS_WORD_LABELS[rxStatusWord]}
        size="small"
        color={RX_STATUS_WORD_COLORS[rxStatusWord]}
        // Focusable so a keyboard or screen-reader user reaches the reason too.
        tabIndex={rxStatusReason ? 0 : undefined}
      />
    );
    return (
      <TableCell>
        {rxStatusReason ? (
          <Tooltip describeChild title={rxStatusReason}>
            {chip}
          </Tooltip>
        ) : (
          chip
        )}
      </TableCell>
    );
  }
  return (
    <TableCell>
      <Chip
        label={inactive ? 'Inactive' : 'Active'}
        size="small"
        color={inactive ? 'default' : 'success'}
        variant={inactive ? 'outlined' : 'filled'}
      />
    </TableCell>
  );
};

/**
 * The "Dispense Status" cell. The numeric 1-8 dispense-status map is a Kroll
 * concept. A Fillware row shows its fill status instead — the server's
 * `pmsGate` verdict as one short word, its sentence as the tooltip
 * (`describePmsGateShort`, PRX-165 B1); a row with no gate reads "Status
 * unavailable", never a guess.
 */
export const DispenseStatusCell: React.FC<RxStatusCellProps> = (props) => {
  const { rxStatusWord, status, adjState, pmsGate } = props;
  if (isUnrecognisedFillware(props)) return <UnrecognisedFillwareCell />;
  // Fillware is told apart by the word here, not by `typeof status` as in
  // `describeDispenseStatus`: this table has `databaseType`, and a null word on
  // Fillware is the unrecognised-letter case handled above, never a Kroll row.
  if (rxStatusWord != null) {
    return (
      <TableCell>
        <FillStatusChip display={describePmsGateShort(pmsGate)} data-testid="rx-pms-gate-chip" />
      </TableCell>
    );
  }
  return (
    <TableCell>
      <Chip label={getDispenseStatusLabel(status)} size="small" color={getDispenseStatusColor(status)} />
      {/* Kroll adjudication flag (raw AdjState column rides on the
          all-prescriptions rows): "Pending Adj"/"Needs Review" fills read
          Status 1 (Active), so the status chip alone hides them. */}
      <RxAdjStateBadge adjState={adjState ?? null} size="small" />
    </TableCell>
  );
};

/** Both cells, adjacent — exists for `RxStatusCell.spec.tsx`, which exercises the pair; the table renders the parts. */
export const RxStatusCell: React.FC<RxStatusCellProps> = (props) => (
  <>
    <RxActiveStatusCell {...props} />
    <DispenseStatusCell {...props} />
  </>
);

export default RxStatusCell;
